import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateFormatter, prepareDoctorName } from 'infrastructure/functions';
import { BaseLog } from 'infrastructure/classes/logs/base-log';
import { DateFormats, PlatformEnums } from 'infrastructure/enums';
import { prepareLogsDateRange } from 'infrastructure/functions/logs';

import useApi from '../use-api';

import type { IPaginatedInteractiveStatusLogs } from 'infrastructure/interfaces/i-iteractive-status-log';
import type { DateRange } from 'infrastructure/types';
import type {
  IPaginationMeta,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';
import type { Dispatch, SetStateAction } from 'react';

const api = (patientGuid: string) =>
  `core-2/patients/${patientGuid}/interaction-status-logs`;

interface IMeta {
  dateRange?: DateRange;
  module: PlatformEnums.RPM | PlatformEnums.CCM;
}

type mergedMeta = IMeta & IPaginationMeta;

type UseLog = {
  loading: boolean;
  meta: mergedMeta;
  items: Array<BaseLog>;
  setMeta: Dispatch<SetStateAction<mergedMeta>>;
};

interface IProps {
  patientGuid: string;
  meta?: IMeta & IPartialPaginationMeta;
}

export const useInteractiveStatusLog = (props: IProps): UseLog => {
  const { patientGuid, meta: metaProps } = props;

  const [meta, setMeta] = useState<mergedMeta>({
    page: 1,
    items: 15,
    totalCount: 0,
    module: PlatformEnums.RPM,
    ...metaProps,
  });

  const { loadData } = useApi();

  const queryKey = [
    api(patientGuid),
    prepareLogsDateRange(meta.dateRange),
    meta.page,
    meta.module,
  ];

  const { data = [], isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      loadData<IPaginatedInteractiveStatusLogs, Omit<IMeta, 'sortField'>>(
        api(patientGuid),
        {
          sortField: JSON.stringify(meta.sortField),
          page: meta.page,
          items: meta.items,
          module: meta.module,
          ...prepareLogsDateRange(meta.dateRange),
        },
      ).then((res) => {
        if (res) {
          const { count, data: rows } = res;

          setMeta({
            ...meta,
            totalCount: count,
          });

          return rows.map((el) => {
            const from = `${el.oldValue}`;
            const to = `${el.newValue}`;
            const who = prepareDoctorName(el.updatedByLogin);
            return new BaseLog({
              who,
              from,
              to,
              date: DateFormatter({
                date: el.createdAt,
                format: DateFormats['MM-dd-yyyy | h:mm a'],
              }),
            });
          });
        }
      }),
    enabled: Boolean(patientGuid),
  });

  return {
    items: data,
    loading: isLoading,
    meta,
    setMeta,
  };
};
